import { alpha } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

export type RachelEmptyConversationSize = 'compact' | 'large';

export const SPARK_STROKE_WIDTH = 0.6;
export const SPARK_ACCENT_STROKE_OPACITY = 0.5;
export const SPARK_MUTED_STROKE_OPACITY = 0.22;

const SPARK_BLURRED_SIZE = 48;
const NEWS_DOT_SIZE = 6;
const HEADLINE_SHIFT_IN_PX = 6;

const SIZE_VARIANTS = {
  compact: {
    sparkSize: 48,
    gapSpacing: 3,
    typographyVariant: 'h6',
  },
  large: {
    sparkSize: 80,
    gapSpacing: 4,
    typographyVariant: 'h1',
  },
} as const;

export const getRachelEmptyConversationSparkSize = (size: RachelEmptyConversationSize) =>
  SIZE_VARIANTS[size].sparkSize;

interface RachelEmptyConversationStylesParams {
  isFocused: boolean;
  size: RachelEmptyConversationSize;
}

const getFocusVariant = ({ isFocused, size }: RachelEmptyConversationStylesParams) => {
  const sizeVariant = SIZE_VARIANTS[size];

  if (isFocused) {
    return {
      sparkSize: sizeVariant.sparkSize,
      sparkTransform: 'none',
      accentOpacity: 1,
      accentTransform: 'none',
      mutedTransform: `translateY(-${HEADLINE_SHIFT_IN_PX}px)`,
      gapSpacing: sizeVariant.gapSpacing,
      typographyVariant: sizeVariant.typographyVariant,
    };
  }

  return {
    sparkSize: SPARK_BLURRED_SIZE,
    sparkTransform: `rotate(180deg) scale(${SPARK_BLURRED_SIZE / sizeVariant.sparkSize})`,
    accentOpacity: 0,
    accentTransform: `translateY(${HEADLINE_SHIFT_IN_PX}px)`,
    mutedTransform: 'none',
    gapSpacing: sizeVariant.gapSpacing + 1,
    typographyVariant: sizeVariant.typographyVariant,
  };
};

export const useRachelEmptyConversationStyles = makeStyles<RachelEmptyConversationStylesParams>()(
  (theme, { isFocused, size }) => {
    const variant = getFocusVariant({ isFocused, size });
    const sparkFrameSize = getRachelEmptyConversationSparkSize(size);
    const mutedOpacity = 1 - variant.accentOpacity;

    return {
      root: {
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(3),
        textAlign: 'center',
        '@media (prefers-reduced-motion: reduce)': { '&, & *': { transition: 'none' } },
      },
      greetingBlock: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.spacing(variant.gapSpacing),
        transition: theme.transitions.create('gap'),
      },
      sparkSlot: {
        position: 'relative',
        display: 'flex',
        flex: 'none',
        alignItems: 'center',
        justifyContent: 'center',
        width: variant.sparkSize,
        height: variant.sparkSize,
        transition: theme.transitions.create(['width', 'height']),
      },
      sparkFrame: {
        position: 'absolute',
        display: 'grid',
        placeItems: 'center',
        width: sparkFrameSize,
        height: sparkFrameSize,
        transform: variant.sparkTransform,
        transition: theme.transitions.create('transform'),
      },
      sparkLayer: {
        gridArea: '1 / 1',
        lineHeight: 0,
        transition: theme.transitions.create('opacity'),
      },
      sparkAccent: {
        opacity: variant.accentOpacity,
      },
      sparkMuted: {
        color: theme.palette.text.primary,
        opacity: mutedOpacity,
      },
      messages: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.spacing(0.5),
      },
      line: {
        fontSize: theme.typography[variant.typographyVariant].fontSize,
        transition: theme.transitions.create(['font-size', 'opacity', 'transform']),
      },
      headlineSlot: {
        display: 'grid',
        justifyItems: 'center',
      },
      headline: {
        gridArea: '1 / 1',
        whiteSpace: 'nowrap',
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        color: 'transparent',
      },
      headlineAccent: {
        backgroundImage: theme.palette.custom.gradients.rachelMark,
        opacity: variant.accentOpacity,
        transform: variant.accentTransform,
      },
      headlineMuted: {
        backgroundImage: `linear-gradient(20deg, ${alpha(theme.palette.text.primary, 0.45)} 0%, ${alpha(theme.palette.text.primary, 0.95)} 100%)`,
        opacity: mutedOpacity,
        transform: variant.mutedTransform,
      },
      news: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(0.5, 1.25),
        borderRadius: `${theme.shape.borderRadiusFull}px`,
        backgroundColor: theme.palette.grey[100],
        textDecoration: 'none',
        transition: theme.transitions.create('background-color', {
          duration: theme.transitions.duration.shorter,
        }),
        '&:hover': { backgroundColor: theme.palette.custom.border.secondary },
      },
      newsDot: {
        width: NEWS_DOT_SIZE,
        height: NEWS_DOT_SIZE,
        flex: 'none',
        borderRadius: '50%',
        backgroundColor: theme.palette.primary.main,
        boxShadow: `0 0 4px 2px ${alpha(theme.palette.primary.main, 0.2)}`,
      },
    };
  },
);
